import {
  collection,
  doc,
  getDoc,
  getDocs,
  addDoc,
  updateDoc,
  deleteDoc,
  setDoc,
  query,
  where,
  serverTimestamp,
  arrayUnion,
  arrayRemove,
} from "firebase/firestore";
import { db } from "./firebase";
import { writeUserNotification } from "./userNotifications";

// ── Prefs (pin + notifications) ───────────────────────────────────────────────

export async function updateChallengeProgressPrefs(challengeId, userId, prefs) {
  await setDoc(
    doc(db, "challengeProgress", `${challengeId}_${userId}`),
    { ...prefs, lastUpdated: serverTimestamp() },
    { merge: true }
  );
}

// Returns [{ challenge, leaderboard }] for challenges the user has pinned
export async function getDashboardChallengesForUser(userId) {
  const snap = await getDocs(
    query(collection(db, "challengeProgress"), where("userId", "==", userId), where("pinnedToDashboard", "==", true))
  );
  if (snap.empty) return [];

  const items = await Promise.all(
    snap.docs.map(async (d) => {
      const p = d.data();
      const cSnap = await getDoc(doc(db, "challenges", p.challengeId));
      if (!cSnap.exists()) return null;
      const challenge = { id: cSnap.id, ...cSnap.data() };
      const today = new Date().toISOString().slice(0, 10);
      if (challenge.status === "completed" && challenge.endDate < today.slice(0, 7)) return null;
      const lbSnap = await getDocs(query(collection(db, "challengeProgress"), where("challengeId", "==", p.challengeId)));
      const leaderboard = lbSnap.docs.map((d) => d.data()).sort((a, b) => (b.total || 0) - (a.total || 0));
      return { challenge, leaderboard };
    })
  );
  return items.filter(Boolean);
}

// ── Internal: notify participants ─────────────────────────────────────────────

async function notifyParticipantsOfProgress(challengeId, triggeringUserId) {
  try {
    const [mySnap, cSnap, allSnap] = await Promise.all([
      getDoc(doc(db, "challengeProgress", `${challengeId}_${triggeringUserId}`)),
      getDoc(doc(db, "challenges", challengeId)),
      getDocs(query(collection(db, "challengeProgress"), where("challengeId", "==", challengeId))),
    ]);
    if (!mySnap.exists() || !cSnap.exists()) return;
    const me = mySnap.data();
    const challengeTitle = cSnap.data().title || "a challenge";
    const toNotify = allSnap.docs.map((d) => d.data()).filter((p) => p.userId !== triggeringUserId && p.notifInApp === true);
    await Promise.all(
      toNotify.map((p) =>
        addDoc(collection(db, "users", p.userId, "notifications"), {
          type: "challenge_progress",
          message: `${me.userName || "Someone"} logged progress in "${challengeTitle}"`,
          fromName: me.userName || "",
          fromAvatar: me.userAvatar || "",
          challengeId,
          read: false,
          createdAt: serverTimestamp(),
        })
      )
    );
  } catch (_) {}
}

function todayKey() {
  return new Date().toISOString().slice(0, 10);
}

function deriveStatus(startDate, endDate) {
  const today = todayKey();
  if (startDate > today) return "upcoming";
  if (endDate < today) return "completed";
  return "active";
}

// ── Admin ─────────────────────────────────────────────────────────────────────

export async function createChallenge(data, adminUid) {
  const status = deriveStatus(data.startDate, data.endDate);
  const participants = data.visibility === "private" ? (data.invitedUsers || []) : [];
  return await addDoc(collection(db, "challenges"), {
    title: data.title?.trim() || "",
    description: data.description?.trim() || "",
    prize: data.prize?.trim() || "",
    type: data.type || "count",
    target: Number(data.target) || 0,
    startDate: data.startDate || "",
    endDate: data.endDate || "",
    visibility: data.visibility || "public",
    participants,
    invitedUsers: data.invitedUsers || [],
    programTag: "momentum",
    metric: data.metric || { label: "", unit: "", trackingMode: "manual", sources: [], maxPerDay: 1 },
    status,
    winnerId: null,
    collectiveReached: false,
    createdBy: adminUid,
    createdAt: serverTimestamp(),
  });
}

export async function updateChallenge(challengeId, updates) {
  if (updates.startDate && updates.endDate) {
    updates.status = deriveStatus(updates.startDate, updates.endDate);
  }
  await updateDoc(doc(db, "challenges", challengeId), updates);
}

export async function deleteChallenge(challengeId) {
  await deleteDoc(doc(db, "challenges", challengeId));
}

export async function getAllChallenges() {
  const snap = await getDocs(collection(db, "challenges"));
  return snap.docs
    .map((d) => ({ id: d.id, ...d.data() }))
    .sort((a, b) => (b.createdAt?.toDate?.()?.getTime?.() || 0) - (a.createdAt?.toDate?.()?.getTime?.() || 0));
}

export async function getChallengeParticipantProgress(challengeId) {
  const snap = await getDocs(
    query(collection(db, "challengeProgress"), where("challengeId", "==", challengeId))
  );
  return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
}

// ── Client ────────────────────────────────────────────────────────────────────

export async function getPublicBrowsableChallenges(userId) {
  const snap = await getDocs(
    query(
      collection(db, "challenges"),
      where("programTag", "==", "momentum"),
      where("visibility", "==", "public")
    )
  );
  const today = todayKey();
  return snap.docs
    .map((d) => ({ id: d.id, ...d.data() }))
    .filter((c) => c.endDate >= today && !(c.participants || []).includes(userId));
}

export async function getUserChallenges(userId) {
  const snap = await getDocs(
    query(collection(db, "challenges"), where("participants", "array-contains", userId))
  );
  const today = todayKey();
  const twoMonthsAgo = new Date();
  twoMonthsAgo.setMonth(twoMonthsAgo.getMonth() - 2);
  const twoMonthsAgoKey = twoMonthsAgo.toISOString().slice(0, 7);
  const order = { active: 0, upcoming: 1, completed: 2 };
  return snap.docs
    .map((d) => {
      const data = { id: d.id, ...d.data() };
      // Re-derive status from dates so an expired challenge never shows as "active".
      // Keep "completed" if Firestore already set it (race finish / collective goal).
      const dateStatus = deriveStatus(data.startDate, data.endDate);
      data.status = data.status === "completed" ? "completed" : dateStatus;
      return data;
    })
    .filter((c) => c.status !== "completed" || c.endDate >= twoMonthsAgoKey)
    .sort((a, b) => (order[a.status] || 0) - (order[b.status] || 0));
}

export async function joinChallenge(challengeId, userId, userName, userAvatar) {
  await updateDoc(doc(db, "challenges", challengeId), {
    participants: arrayUnion(userId),
  });
  await setDoc(
    doc(db, "challengeProgress", `${challengeId}_${userId}`),
    {
      challengeId,
      userId,
      userName: userName || "",
      userAvatar: userAvatar || "",
      total: 0,
      dailyLog: {},
      manualEntries: [],
      lastUpdated: serverTimestamp(),
    },
    { merge: true }
  );
}

export async function leaveChallenge(challengeId, userId) {
  await updateDoc(doc(db, "challenges", challengeId), {
    participants: arrayRemove(userId),
  });
}

export async function getChallengeLeaderboard(challengeId) {
  const snap = await getDocs(
    query(collection(db, "challengeProgress"), where("challengeId", "==", challengeId))
  );
  return snap.docs
    .map((d) => ({ id: d.id, ...d.data() }))
    .sort((a, b) => (b.total || 0) - (a.total || 0));
}

export async function getUserChallengeProgress(challengeId, userId) {
  const snap = await getDoc(doc(db, "challengeProgress", `${challengeId}_${userId}`));
  return snap.exists() ? { id: snap.id, ...snap.data() } : null;
}

// ── Manual entry ──────────────────────────────────────────────────────────────

export async function addManualEntry(challengeId, userId, dateKey, value, note) {
  const progressRef = doc(db, "challengeProgress", `${challengeId}_${userId}`);
  const snap = await getDoc(progressRef);
  const existing = snap.exists() ? snap.data() : { total: 0, manualEntries: [] };
  const numValue = Math.max(1, Number(value) || 1);
  const newTotal = (existing.total || 0) + numValue;

  await setDoc(
    progressRef,
    {
      total: newTotal,
      manualEntries: [
        ...(existing.manualEntries || []),
        { date: dateKey, value: numValue, note: note || "", loggedAt: new Date().toISOString() },
      ],
      lastUpdated: serverTimestamp(),
    },
    { merge: true }
  );

  await checkChallengeCompletion(challengeId, userId, newTotal);
  notifyParticipantsOfProgress(challengeId, userId); // fire-and-forget
}

// ── Auto progress ─────────────────────────────────────────────────────────────

// sourceType: "workout_logged" | "side_quest" | "habit" | "checkin"
// opts.questTitle — for side_quest, to match side_quest_specific sources
// opts.habitCategory — for habit, to match habit_specific sources
export async function recordAutoProgress(userId, sourceType, dateKey, { questTitle, habitCategory } = {}) {
  const snap = await getDocs(
    query(
      collection(db, "challenges"),
      where("participants", "array-contains", userId),
      where("status", "==", "active")
    )
  );

  const active = snap.docs
    .map((d) => ({ id: d.id, ...d.data() }))
    .filter((c) => c.startDate <= dateKey && c.endDate >= dateKey && c.metric?.trackingMode === "auto");

  for (const challenge of active) {
    const sources = challenge.metric?.sources || [];

    const match = sources.find((s) => {
      if (sourceType === "side_quest") {
        return s.type === "side_quest_any" || (s.type === "side_quest_specific" && s.questTitle === questTitle);
      }
      if (sourceType === "habit") {
        return s.type === "habit_any" || (s.type === "habit_specific" && s.category === habitCategory);
      }
      return s.type === sourceType;
    });
    if (!match) continue;

    const progressRef = doc(db, "challengeProgress", `${challenge.id}_${userId}`);
    const pSnap = await getDoc(progressRef);
    const progress = pSnap.exists() ? pSnap.data() : { total: 0, dailyLog: {} };

    const todayLog = progress.dailyLog?.[dateKey] || {};
    const sourceKey = match.type;
    const currentCount = todayLog[sourceKey] || 0;
    if (currentCount >= (match.maxPerDay || 1)) continue;

    const overallCap = challenge.metric?.maxPerDay;
    const totalToday = Object.values(todayLog).reduce((a, b) => a + b, 0);
    if (overallCap && totalToday >= overallCap) continue;

    const newTotal = (progress.total || 0) + 1;

    await setDoc(
      progressRef,
      {
        challengeId: challenge.id,
        userId,
        total: newTotal,
        dailyLog: {
          ...(progress.dailyLog || {}),
          [dateKey]: { ...todayLog, [sourceKey]: currentCount + 1 },
        },
        lastUpdated: serverTimestamp(),
      },
      { merge: true }
    );

    await checkChallengeCompletion(challenge.id, userId, newTotal);
    notifyParticipantsOfProgress(challenge.id, userId); // fire-and-forget
  }
}

// Mirror of recordAutoProgress — removes a point when a tracked action is undone
export async function removeAutoProgress(userId, sourceType, dateKey, { questTitle, habitCategory } = {}) {
  const snap = await getDocs(
    query(
      collection(db, "challenges"),
      where("participants", "array-contains", userId),
      where("status", "==", "active")
    )
  );

  const active = snap.docs
    .map((d) => ({ id: d.id, ...d.data() }))
    .filter((c) => c.startDate <= dateKey && c.endDate >= dateKey && c.metric?.trackingMode === "auto");

  for (const challenge of active) {
    const sources = challenge.metric?.sources || [];
    const match = sources.find((s) => {
      if (sourceType === "side_quest") {
        return s.type === "side_quest_any" || (s.type === "side_quest_specific" && s.questTitle === questTitle);
      }
      if (sourceType === "habit") {
        return s.type === "habit_any" || (s.type === "habit_specific" && s.category === habitCategory);
      }
      return s.type === sourceType;
    });
    if (!match) continue;

    const progressRef = doc(db, "challengeProgress", `${challenge.id}_${userId}`);
    const pSnap = await getDoc(progressRef);
    if (!pSnap.exists()) continue;
    const progress = pSnap.data();

    const todayLog = progress.dailyLog?.[dateKey] || {};
    const sourceKey = match.type;
    const currentCount = todayLog[sourceKey] || 0;
    if (currentCount <= 0) continue;

    await setDoc(
      progressRef,
      {
        total: Math.max(0, (progress.total || 0) - 1),
        dailyLog: {
          ...(progress.dailyLog || {}),
          [dateKey]: { ...todayLog, [sourceKey]: currentCount - 1 },
        },
        lastUpdated: serverTimestamp(),
      },
      { merge: true }
    );
  }
}

async function checkChallengeCompletion(challengeId, userId, newTotal) {
  const cSnap = await getDoc(doc(db, "challenges", challengeId));
  if (!cSnap.exists()) return;
  const c = cSnap.data();

  if (c.type === "race" && c.target && newTotal >= c.target && !c.winnerId) {
    await updateDoc(doc(db, "challenges", challengeId), { status: "completed", winnerId: userId });
    return;
  }

  if (c.type === "collective" && c.target && !c.collectiveReached) {
    const allProgress = await getChallengeLeaderboard(challengeId);
    const combined = allProgress.reduce((sum, p) => sum + (p.total || 0), 0);
    if (combined >= c.target) {
      await updateDoc(doc(db, "challenges", challengeId), { status: "completed", collectiveReached: true });
    }
  }
}

// ── End-of-challenge finalization ─────────────────────────────────────────────

// Called when the Challenges screen loads. Finds any challenges that passed
// their end date, marks them completed, determines the winner, and sends
// in-app notifications to all participants (once per challenge via notifiedEndAt).
export async function finalizeEndedChallenges(userId) {
  if (!userId) return;
  const snap = await getDocs(
    query(
      collection(db, "challenges"),
      where("participants", "array-contains", userId),
      where("status", "==", "active")
    )
  );
  const today = todayKey();
  const ended = snap.docs
    .map((d) => ({ id: d.id, ...d.data() }))
    .filter((c) => c.endDate < today && !c.notifiedEndAt);

  for (const challenge of ended) {
    await _doFinalizeChallenge(challenge);
  }
}

async function _doFinalizeChallenge(challenge) {
  const progressSnap = await getDocs(
    query(collection(db, "challengeProgress"), where("challengeId", "==", challenge.id))
  );
  const progress = progressSnap.docs
    .map((d) => d.data())
    .sort((a, b) => (b.total || 0) - (a.total || 0));

  let winnerId = challenge.winnerId || null;
  let winnerName = null;
  let winnerAvatar = null;
  let collectiveReached = challenge.collectiveReached || false;

  if ((challenge.type === "count" || challenge.type === "race") && !winnerId) {
    const top = progress[0];
    if (top && (top.total || 0) > 0) {
      winnerId = top.userId;
      winnerName = top.userName || null;
      winnerAvatar = top.userAvatar || null;
    }
  } else if (challenge.type === "count" || challenge.type === "race") {
    const winner = progress.find((p) => p.userId === winnerId);
    winnerName = winner?.userName || null;
    winnerAvatar = winner?.userAvatar || null;
  } else if (challenge.type === "collective") {
    const combined = progress.reduce((sum, p) => sum + (p.total || 0), 0);
    collectiveReached = challenge.target ? combined >= challenge.target : false;
  }

  const updates = {
    status: "completed",
    notifiedEndAt: serverTimestamp(),
    collectiveReached,
  };
  if (winnerId && !challenge.winnerId) updates.winnerId = winnerId;
  await updateDoc(doc(db, "challenges", challenge.id), updates);

  const participants = challenge.participants || [];
  const message = _buildEndMessage(challenge, winnerName, collectiveReached);
  await Promise.all(
    participants.map((uid) =>
      writeUserNotification({
        toUserId: uid,
        type: "challenge_ended",
        message,
        fromName: "Challenge Results",
        fromAvatar: winnerAvatar || "",
        screen: "challenges",
      })
    )
  );
}

function _buildEndMessage(challenge, winnerName, collectiveReached) {
  const t = `"${challenge.title}"`;
  if (challenge.type === "collective") {
    return collectiveReached
      ? `${t} — The group reached the goal! Great teamwork! 🎉`
      : `${t} has ended. The group didn't reach the goal this time — keep it up!`;
  }
  if (winnerName) return `${t} has ended! Winner: ${winnerName} 🏆`;
  return `${t} has ended.`;
}
